import React, { useEffect, useState, useRef, useCallback, memo } from 'react';
import {
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Box,
    Link,
    Divider,
    Switch
} from '@mui/material';
import { WbSunnyOutlined as LightMode, DarkModeOutlined } from '@mui/icons-material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore, useSettingsStore, useApiStore } from '@core/store';
import { MENU_DATA } from '@core/data/menuData';
import { UserMenuBlock } from '@/components/shared/UserMenuBlock';
import { TEST_IDS } from '@core/constants/testIds.ts';

interface SidebarProps {
    open: boolean;
    onClose: () => void;
    isMobile?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = memo(({ open, onClose }) => {
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const isManualNavigationRef = useRef(false);
    const { hasLicense, hasRole } = useAuthStore();
    const { hasModule, hasComponent, gatewayIds } = useApiStore();
    const { darkMode, toggleDarkMode, isMobile } = useSettingsStore();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const menus = MENU_DATA;

    const isRouteMatch = useCallback((currentPath: string, menuPath: string): boolean => {
        if (!menuPath) return false;

        const cleanCurrent = currentPath.split('?')[0].split('#')[0];
        const cleanMenu = menuPath.split('?')[0].split('#')[0];

        if (cleanCurrent === cleanMenu) return true;

        if (cleanMenu.includes(':')) {
            const menuParts = cleanMenu.split('/');
            const currentParts = cleanCurrent.split('/');

            if (menuParts.length !== currentParts.length) return false;

            return menuParts.every((part, index) => part.startsWith(':') || part === currentParts[index]);
        }

        return false;
    }, []);

    const scrollToActiveItem = useCallback((): void => {
        // Skip scroll if user manually clicked sidebar
        if (isManualNavigationRef.current) {
            setTimeout(() => {
                isManualNavigationRef.current = false;
            }, 100);
            return;
        }

        setTimeout(() => {
            const scrollContainer = scrollAreaRef.current;
            if (!scrollContainer) return;

            // Find active sub-item
            const activeSubItem = scrollContainer.querySelector('.sidebar-menu-sub-item.active');

            if (activeSubItem) {
                const containerRect = scrollContainer.getBoundingClientRect();
                const itemRect = activeSubItem.getBoundingClientRect();
                const scrollTop = scrollContainer.scrollTop;

                // Calculate target scroll position to center the item
                const targetScrollTop =
                    scrollTop + (itemRect.top - containerRect.top) - containerRect.height / 2 + itemRect.height / 2;

                scrollContainer.scrollTo({
                    top: targetScrollTop,
                    behavior: 'smooth'
                });
            }
        }, 600);
    }, []);

    const showMenuItem = (item: any): boolean => {
        if (!item) return false;
        if (item.skipMigration) return false;

        // For development purposes, show all items.
        // return true;

        return (
            !item.hidden &&
            (!item.license || hasLicense(item.license)) &&
            (!item.role || hasRole(item.role)) &&
            (!item.module || hasModule(item.module)) &&
            (!item.component || hasComponent(item.component)) &&
            (!item.requiresGateway || gatewayIds.length > 0)
        );
    };

    const handleItemClick = (item: any, categoryKey: string, originalItemIndex: number): void => {
        const key: string = `${categoryKey}-${originalItemIndex}`;

        if (hasVisibleChildren(item)) {
            setExpandedKey(expandedKey === key ? null : key);

            if (item.path) {
                isManualNavigationRef.current = true;
                navigate(item.path);
                if (isMobile) {
                    onClose();
                }
            }
        } else if (item.path) {
            isManualNavigationRef.current = true;
            navigate(item.path);
            if (isMobile) {
                onClose();
            }
        }
    };

    const handleSubItemClick = (subItem: any): void => {
        if (subItem.path) {
            isManualNavigationRef.current = true;
            navigate(subItem.path);
            if (isMobile) {
                onClose();
            }
        }
    };

    const isActive = (path: string): boolean => {
        if (!path) return false;
        return isRouteMatch(location.pathname, path);
    };

    const isMenuItemActive = (item: any): boolean => {
        if (item.path && isActive(item.path)) return true;

        if (item.items) {
            return item.items.some((subItem: any) => isMenuItemActive(subItem));
        }

        return false;
    };

    const hasVisibleChildren = (item: any): boolean => {
        if (!item.items || item.items.length === 0) return false;
        return item.items.some((subItem: any) => showMenuItem(subItem));
    };

    useEffect(() => {
        let foundExpandedKey: string | null = null;
        let shouldScroll = false;

        Object.entries(menus).forEach(([categoryKey, category]) => {
            if (!category.items) return;

            category.items.forEach((item: any, originalItemIndex: number) => {
                if (!showMenuItem(item)) return;

                const menuKey: string = `${categoryKey}-${originalItemIndex}`;

                if (foundExpandedKey === null && isMenuItemActive(item)) {
                    foundExpandedKey = menuKey;
                    shouldScroll = true;
                }
            });
        });

        if (foundExpandedKey !== null && foundExpandedKey !== expandedKey) {
            setExpandedKey(foundExpandedKey);
        }

        if (shouldScroll && !isManualNavigationRef.current) {
            scrollToActiveItem();
        }
    }, [location.pathname, isRouteMatch, scrollToActiveItem]);

    useEffect(() => {
        if (expandedKey && !isManualNavigationRef.current) {
            scrollToActiveItem();
        }
    }, [expandedKey, scrollToActiveItem]);

    return (
        <Drawer
            variant={isMobile ? 'temporary' : 'persistent'}
            anchor="left"
            open={open}
            onClose={onClose}
            className="Vuely-sidebar"
            ModalProps={{
                keepMounted: true
            }}
        >
            <Box
                className="sidebar transparent"
                sx={{
                    height: '100%',
                    overflow: 'hidden'
                }}
            >
                <Box
                    className="scroll-area"
                    ref={scrollAreaRef}
                    sx={{
                        position: 'relative',
                        height: '100%',
                        overflowY: 'auto',
                        scrollBehavior: 'smooth'
                    }}
                >
                    <Box className="transparent navigation" sx={{ paddingBottom: '120px' }}>
                        <List sx={{ padding: 0 }}>
                            {isMobile && <UserMenuBlock />}

                            {/* Menu Categories */}
                            {Object.entries(menus).map(([categoryKey, category]: [string, any]) => {
                                if (!showMenuItem(category)) return null;

                                return (
                                    <Box key={categoryKey}>
                                        {/* Category Title */}
                                        {categoryKey && (
                                            <Box className="sidebar-title">
                                                <span
                                                    style={{
                                                        color: 'rgba(0, 0, 0, 0.6)',
                                                        textTransform: 'uppercase',
                                                        fontWeight: 600,
                                                        letterSpacing: 1,
                                                        fontSize: '0.75rem',
                                                        padding: '8px 16px',
                                                        marginTop: '16px',
                                                        display: 'block'
                                                    }}
                                                >
                                                    {t(categoryKey)}
                                                </span>
                                            </Box>
                                        )}

                                        {/* Menu Items */}
                                        {category.items?.map((item: any, originalItemIndex: number) => {
                                            if (!showMenuItem(item)) return null;

                                            const key: string = `${categoryKey}-${originalItemIndex}`;
                                            const isExpanded: boolean = expandedKey === key;
                                            const itemIsActive: boolean = isMenuItemActive(item);
                                            const iconClass: string =
                                                (isExpanded || itemIsActive) && item.activeAction
                                                    ? item.activeAction
                                                    : item.action;

                                            return (
                                                <Box key={key}>
                                                    {item.path ? (
                                                        <Link
                                                            component={RouterLink}
                                                            to={item.path}
                                                            sx={{
                                                                textDecoration: 'none',
                                                                color: 'inherit',
                                                                display: 'block'
                                                            }}
                                                        >
                                                            <ListItemButton
                                                                onClick={() =>
                                                                    handleItemClick(
                                                                        item,
                                                                        categoryKey,
                                                                        originalItemIndex
                                                                    )
                                                                }
                                                                className={`sidebar-menu-item${isExpanded || itemIsActive ? ' active' : ''}`}
                                                            >
                                                                <ListItemIcon sx={{ padding: '7px' }}>
                                                                    {iconClass?.startsWith('bi') ? (
                                                                        <i
                                                                            className={iconClass}
                                                                            style={{ fontSize: '22px' }}
                                                                        />
                                                                    ) : (
                                                                        <i
                                                                            className={`zmdi ${iconClass}`}
                                                                            style={{ fontSize: '22px' }}
                                                                        />
                                                                    )}
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={t(item.title)}
                                                                    sx={{
                                                                        '& .MuiListItemText-primary': {
                                                                            fontWeight: 700
                                                                        }
                                                                    }}
                                                                />
                                                            </ListItemButton>
                                                        </Link>
                                                    ) : (
                                                        <ListItemButton
                                                            onClick={() =>
                                                                handleItemClick(item, categoryKey, originalItemIndex)
                                                            }
                                                            className={`sidebar-menu-item${
                                                                isExpanded || itemIsActive ? ' active' : ''
                                                            }`}
                                                            sx={{
                                                                fontWeight: 700
                                                            }}
                                                        >
                                                            <ListItemIcon sx={{ padding: '7px' }}>
                                                                {iconClass?.startsWith('bi') ? (
                                                                    <i
                                                                        className={iconClass}
                                                                        style={{ fontSize: '20px' }}
                                                                    />
                                                                ) : (
                                                                    <i
                                                                        className={`zmdi ${iconClass}`}
                                                                        style={{ fontSize: '20px' }}
                                                                    />
                                                                )}
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={t(item.title)}
                                                                sx={{
                                                                    '& .MuiListItemText-primary': {
                                                                        fontWeight: 700
                                                                    }
                                                                }}
                                                            />
                                                        </ListItemButton>
                                                    )}

                                                    {/* Sub-menu items */}
                                                    {hasVisibleChildren(item) && (
                                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                            <List component="div" disablePadding>
                                                                {item.items
                                                                    .filter((subItem: any) => showMenuItem(subItem))
                                                                    .map((subItem: any, subIndex: number) => {
                                                                        if (subItem.nonClickable) {
                                                                            return (
                                                                                <Box key={subIndex}>
                                                                                    <ListItemText
                                                                                        primary={t(subItem.title)}
                                                                                        className="sidebar-menu-subheading"
                                                                                        sx={{
                                                                                            '& .MuiListItemText-primary':
                                                                                                {
                                                                                                    fontWeight: 700
                                                                                                }
                                                                                        }}
                                                                                    />
                                                                                    {subItem.items && (
                                                                                        <List
                                                                                            component="div"
                                                                                            disablePadding
                                                                                        >
                                                                                            {subItem.items
                                                                                                .filter(
                                                                                                    (subSubItem: any) =>
                                                                                                        showMenuItem(
                                                                                                            subSubItem
                                                                                                        )
                                                                                                )
                                                                                                .map(
                                                                                                    (
                                                                                                        subSubItem: any,
                                                                                                        subSubIndex: number
                                                                                                    ) => {
                                                                                                        const subSubItemIsActive: boolean =
                                                                                                            isActive(
                                                                                                                subSubItem.path
                                                                                                            );

                                                                                                        return (
                                                                                                            <Link
                                                                                                                key={
                                                                                                                    subSubIndex
                                                                                                                }
                                                                                                                component={
                                                                                                                    RouterLink
                                                                                                                }
                                                                                                                to={
                                                                                                                    subSubItem.path ||
                                                                                                                    '#'
                                                                                                                }
                                                                                                                sx={{
                                                                                                                    textDecoration:
                                                                                                                        'none',
                                                                                                                    color: 'inherit',
                                                                                                                    display:
                                                                                                                        'block'
                                                                                                                }}
                                                                                                            >
                                                                                                                <ListItemButton
                                                                                                                    onClick={() =>
                                                                                                                        handleSubItemClick(
                                                                                                                            subSubItem
                                                                                                                        )
                                                                                                                    }
                                                                                                                    className={`sidebar-menu-item sidebar-menu-sub-item sidebar-menu-subheading-item${
                                                                                                                        subSubItemIsActive
                                                                                                                            ? ' active'
                                                                                                                            : ''
                                                                                                                    }`}
                                                                                                                    sx={{
                                                                                                                        fontWeight:
                                                                                                                            subSubItemIsActive
                                                                                                                                ? 700
                                                                                                                                : 400,
                                                                                                                        '& .MuiListItemText-primary':
                                                                                                                            {
                                                                                                                                fontWeight:
                                                                                                                                    subSubItemIsActive
                                                                                                                                        ? 700
                                                                                                                                        : 400
                                                                                                                            }
                                                                                                                    }}
                                                                                                                >
                                                                                                                    <ListItemText
                                                                                                                        primary={t(
                                                                                                                            subSubItem.title
                                                                                                                        )}
                                                                                                                    />
                                                                                                                </ListItemButton>
                                                                                                            </Link>
                                                                                                        );
                                                                                                    }
                                                                                                )}
                                                                                        </List>
                                                                                    )}
                                                                                </Box>
                                                                            );
                                                                        }

                                                                        const subItemIsActive: boolean = isActive(
                                                                            subItem.path
                                                                        );

                                                                        return (
                                                                            <Link
                                                                                key={subIndex}
                                                                                component={RouterLink}
                                                                                to={subItem.path || '#'}
                                                                                sx={{
                                                                                    textDecoration: 'none',
                                                                                    color: 'inherit',
                                                                                    display: 'block'
                                                                                }}
                                                                            >
                                                                                <ListItemButton
                                                                                    onClick={() =>
                                                                                        handleSubItemClick(subItem)
                                                                                    }
                                                                                    className={`sidebar-menu-item sidebar-menu-sub-item${
                                                                                        subItemIsActive ? ' active' : ''
                                                                                    }`}
                                                                                    sx={{
                                                                                        fontWeight: subItemIsActive
                                                                                            ? 700
                                                                                            : 400,
                                                                                        '& .MuiListItemText-primary': {
                                                                                            fontWeight: subItemIsActive
                                                                                                ? 700
                                                                                                : 400
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <ListItemText
                                                                                        primary={t(subItem.title)}
                                                                                    />
                                                                                </ListItemButton>
                                                                            </Link>
                                                                        );
                                                                    })}
                                                            </List>
                                                        </Collapse>
                                                    )}
                                                    <Divider
                                                        sx={{
                                                            borderColor: 'var(--sidebar-divider)',
                                                            bgcolor: '1px',
                                                            mr: 1
                                                        }}
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                );
                            })}

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    paddingRight: '16px',
                                    paddingTop: '16px'
                                }}
                            >
                                <ListItemText
                                    primary={t('theme')}
                                    sx={{
                                        '& .MuiListItemText-primary': {
                                            paddingLeft: '40px',
                                            fontWeight: 700
                                        }
                                    }}
                                />
                                <Box>
                                    <Switch
                                        data-testid={TEST_IDS.HEADER.DARK_MODE_SWITCH}
                                        checked={darkMode}
                                        onChange={toggleDarkMode}
                                        className="theme-switch"
                                        icon={<i className="bi bi-brightness-high theme-switch-light-icon" />}
                                        checkedIcon={<i className="bi bi-moon theme-switch-dark-icon" />}
                                    />
                                </Box>
                            </Box>
                        </List>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
});

Sidebar.displayName = 'Sidebar';
